import React, {
  useEffect, useMemo, useState,
} from 'react';

import {
  Skeleton, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';

import { useGetLessonTemplateQuery } from '../../../services/lessons';
import { SubjectColorKeys } from '../../../theme';
import {
  getLessonClass,
  getLessonClassName,
  getLessonCode, getLessonCourse, getLessonSubject,
} from '../../../utils/common/classes';
import { SUBJECTS } from '../../../utils/common/constants';
import { EntryText, TimesheetEntry } from '../index.styles';
import { TimesheetLessonDetails } from '../types';

interface LessonEntryProps {
  startDatetime: number;
  lesson: TimesheetLessonDetails;
  onClick?: () => void;
  renderTimesheetedOnly?: boolean;
}

const LessonEntry = ({
  startDatetime,
  lesson,
  onClick,
  renderTimesheetedOnly = false,
}: LessonEntryProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // We need to evaluate the endDatetime in this component as we require
  // the duration attribute from lessonData
  const [endDatetime, setEndDatetime] = useState<number>(startDatetime);
  const { data: lessonData, isLoading } = useGetLessonTemplateQuery({
    course: getLessonCourse(lesson),
    lessonId: getLessonCode(lesson),
  });

  useEffect(() => {
    if (lessonData) {
      setEndDatetime(dayjs(startDatetime).add(lessonData.data.duration, 'minute').valueOf());
    }
  }, [lessonData, startDatetime]);

  const timeDescription = useMemo(() =>
    `${dayjs(startDatetime).format('h:mm a')} - ${dayjs(endDatetime).format('h:mm a')}`,
  [startDatetime, endDatetime]);

  if (isLoading || !lessonData) {
    return (
      <Skeleton variant="rectangular" height={32} />
    );
  }

  // Exclude any entries which are not timesheeted if the flag is set true
  if (renderTimesheetedOnly && !lessonData.data.isTimesheeted) {
    return null;
  }

  return (
    <TimesheetEntry
      $color={SUBJECTS[getLessonSubject(lesson)].colorKey as SubjectColorKeys}
      onClick={onClick}
    >
      <Stack direction="column" spacing={1}>
        <EntryText variant="caption">
          {`${isMd ? timeDescription : timeDescription.split(' - ')[0]}`}
        </EntryText>
        <EntryText variant="caption">
          {isMd ? getLessonClassName(lesson) : getLessonClass(lesson)}
        </EntryText>
        <EntryText variant="caption">
          {isMd ? lesson.teacherName : lesson.shortTeacherName}
        </EntryText>
      </Stack>
    </TimesheetEntry>
  );
};

export default LessonEntry;
